/**
 * The document row's actions (#6559): the ⋮ / right-click menu with its
 * Delete dialog, shared by the table, the grid and Home so the three
 * surfaces never drift; and the folder menu beside it. No Unlock: a locked
 * document is unlocked by opening it (#11856), so the row's click is the
 * affordance and the menu carries nothing for it.
 *
 * The menu is the complete list, so it repeats what the hover strip already
 * shows — a shortcut and a complete list are a hierarchy, not an ambiguity,
 * because the two are never on screen together. It does NOT offer Open: the
 * row is the Open control, and the row IS visible whenever the menu is
 * (Claude Design, round 7g).
 */
import React, { useState } from "react";
import { Menu } from "@/components/FpPages/RMS/design";
import { BASE_API_URL } from "@/constants";
import fpFetch from "@/services/fpFetch";
import deleteUserUploadedFile from "@/components/FpAppV4/services/deleteUserUploadedFile";
import ConfirmDialog from "../ConfirmDialog";
import { displayFileName } from "./rows";
import type { DocumentRow } from "./api";
import { documentShareHref, isReservedRootFolder } from "./rows";

export interface MenuTarget<T> {
  item: T;
  anchor: HTMLElement | null;
  position: { top: number; left: number } | null;
}

/** Where a menu opens from: the ⋮ button (anchored) or a right-click (at
 *  the pointer). */
export const targetFromEvent = <T,>(
  item: T,
  e: React.MouseEvent
): MenuTarget<T> =>
  e.type === "contextmenu"
    ? { item, anchor: null, position: { top: e.clientY, left: e.clientX } }
    : { item, anchor: e.currentTarget as HTMLElement, position: null };

/** Fetch the blob and hand it to the browser as a download. */
export const downloadDocument = async (row: DocumentRow): Promise<void> => {
  const response = await fpFetch(
    `${BASE_API_URL}/userData/fp0065?fileId=${row.fileId}&v=cacheBust8&raw=true`
  );
  if (!response?.ok) {
    throw new Error(`Download failed with status ${response?.status}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = row.fileName || "download";
  a.click();
  URL.revokeObjectURL(url);
};

export interface RowMenuOptions {
  /** Still needed for the phone's row tap, which routes through the table
   *  rather than this menu; the menu itself no longer offers Open. */
  onOpenFile: (row: DocumentRow) => void;
  /** `focus` names a document to point at on arrival — "Show in folder" lands
   *  in a folder of hundreds and has to say which row it meant. */
  onOpenFolder: (path: string[], focus?: string) => void;
  /** Show the row in the details pane — the way back in once the pane has
   *  been closed (Claude Design, round 4). */
  onDetails?: (row: DocumentRow) => void;
  /** Move the one document (round 19j, Hunter): the selection toolbar's
   *  Move, reachable without selecting. Absent where the reader cannot
   *  edit. */
  onMove?: (row: DocumentRow) => void;
  /** Offer "Show in folder": only meaningful when rows come from anywhere. */
  showLocation: boolean;
  onRowsChanged: () => void;
}

/** The document menu as a hook: `open` from a row's ⋮ or right-click, and
 *  `element` to render once beside the listing. */
export const useRowMenu = ({
  onOpenFile,
  onOpenFolder,
  onDetails,
  onMove,
  showLocation,
  onRowsChanged,
}: RowMenuOptions) => {
  const [menu, setMenu] = useState<MenuTarget<DocumentRow> | null>(null);
  const [deleting, setDeleting] = useState<DocumentRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const close = () => setMenu(null);
  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteUserUploadedFile([deleting.fileId]);
      onRowsChanged();
    } finally {
      setDeleteBusy(false);
      setDeleting(null);
    }
  };
  const row = menu?.item ?? null;
  const element = (
    <>
      <Menu
        open={menu !== null}
        onClose={close}
        anchorEl={menu?.anchor ?? null}
        anchorPosition={menu?.position ?? null}
        label="Document actions"
        items={[
          ...(onDetails
            ? [
                {
                  label: "Details",
                  onSelect: () => {
                    if (row) onDetails(row);
                  },
                },
              ]
            : []),
          ...(showLocation
            ? [
                {
                  label: "Show in folder",
                  onSelect: () => {
                    if (row) onOpenFolder(row.filePath, row.fileId);
                  },
                },
              ]
            : []),
          {
            label: "Download",
            onSelect: () => {
              if (row) void downloadDocument(row);
            },
          },
          ...(onMove
            ? [
                {
                  label: "Move",
                  onSelect: () => {
                    if (row) onMove(row);
                  },
                },
              ]
            : []),
          // Copy link lives here and never on the selection toolbar: a link
          // addresses exactly one document, so the action is meaningless
          // against a set, and one that appeared and vanished as the count
          // crossed 1 would be worse than one that lives somewhere stable
          // (Claude Design, round 7h).
          {
            label: "Copy link",
            onSelect: () => {
              // Guarded: `navigator.clipboard` is undefined on a non-secure
              // origin, where reaching for it throws synchronously.
              if (row && navigator.clipboard)
                void navigator.clipboard.writeText(
                  new URL(
                    documentShareHref(row),
                    window.location.origin
                  ).toString()
                );
            },
          },
          {
            label: "Delete",
            destructive: true,
            onSelect: () => {
              if (row) setDeleting(row);
            },
          },
        ]}
      />
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
        title="Delete document"
        message={
          deleting
            ? `${displayFileName(deleting.fileName)} will be deleted.`
            : ""
        }
        confirmLabel="Delete"
        busy={deleteBusy}
      />
    </>
  );
  return { open: setMenu, isOpen: menu !== null, element };
};

export type FolderAction = "rename" | "download" | "delete";

/** The folder menu: Open, then the folder's own actions when the owner
 *  handles them (the page does; the picker does not). */
export const useFolderMenu = (
  onOpenFolder: (path: string[]) => void,
  onFolderAction?: (action: FolderAction, path: string[]) => void
) => {
  const [menu, setMenu] = useState<MenuTarget<string[]> | null>(null);
  const close = () => setMenu(null);
  const path = menu?.item ?? null;
  // The synthesized Processing folder has no rename, download or delete: the
  // routes refuse them, so the menu does not offer them.
  const actionable =
    onFolderAction !== undefined &&
    path !== null &&
    !isReservedRootFolder(path);
  const act = (action: FolderAction) => () => {
    if (path) onFolderAction?.(action, path);
  };
  const element = (
    <Menu
      open={menu !== null}
      onClose={close}
      anchorEl={menu?.anchor ?? null}
      anchorPosition={menu?.position ?? null}
      label="Folder actions"
      items={[
        // A folder row opens on click too, but its menu keeps Open: a
        // reserved root folder has no other item, and a menu with nothing in
        // it is worse than a repeated verb.
        {
          label: "Open",
          onSelect: () => {
            if (path) onOpenFolder(path);
          },
        },
        ...(actionable
          ? [
              { label: "Rename", onSelect: act("rename") },
              { label: "Download", onSelect: act("download") },
              {
                label: "Delete",
                destructive: true,
                onSelect: act("delete"),
              },
            ]
          : []),
      ]}
    />
  );
  return { open: setMenu, isOpen: menu !== null, element };
};
